import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookingLink } from '@/components/BookingLink';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';
import type { Trip, TripPlan } from '@shared/types';

export function PrintPlanPage() {
  const { tripId } = useParams();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [plan, setPlan] = useState<TripPlan | null>(null);

  useEffect(() => {
    if (!tripId) return;
    api.trip(tripId).then((data) => {
      setTrip(data.trip);
      setPlan(data.plan);
    });
  }, [tripId]);

  if (!trip || !plan) return <p className="text-sm text-muted">Preparing print plan…</p>;

  return (
    <article className="space-y-4 bg-sand p-1">
      <div className="no-print mb-3">
        <button className="w-full rounded-card bg-navy px-4 py-3 text-sm font-semibold text-sand" onClick={() => window.print()}>
          Print or save as PDF
        </button>
        <p className="mt-2 text-[12px] text-muted">Chrome only for QA — Chip does not use Safari. Use the browser print dialog → Save as PDF.</p>
      </div>
      <header>
        <p className="text-[11px] tracking-[0.18em] text-brass">CONCOURSE TRAVEL</p>
        <h1 className="font-display text-3xl text-navy">{plan.headline}</h1>
        <p className="mt-2 text-sm">
          {trip.originIata} → {trip.destIata} · {trip.airline} · {trip.cabin}
        </p>
        <p className="text-[12px] text-muted">Playbook seed {plan.playbookVerifiedAt}. Not live inventory or prices.</p>
      </header>
      <ChipRow>
        {plan.recommendations
          .filter((r) => r.action === 'use')
          .map((r) => (
            <Chip key={r.serviceId} tone="success">
              {r.airportIata} {r.serviceName}
            </Chip>
          ))}
      </ChipRow>
      <section>
        <h2 className="font-display text-2xl text-navy">Recommendations</h2>
        {plan.recommendations.map((rec) => (
          <div key={rec.headline} className="mt-3 border-t border-line pt-2">
            <p className="text-sm font-semibold">
              {rec.action.toUpperCase()} · {rec.airportIata} · {rec.serviceName}
            </p>
            <p className="text-sm">{rec.headline}</p>
            <p className="text-[13px] text-muted">{rec.estimatedCost.label}</p>
            <ul className="list-disc pl-4 text-[13px]">
              {rec.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
            {rec.bookingUrl && (
              <p className="mt-1 text-[13px]">
                Reserve:{' '}
                <a href={rec.bookingUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-navy underline">
                  {rec.bookingUrl}
                </a>
              </p>
            )}
          </div>
        ))}
      </section>
      <section>
        <h2 className="font-display text-2xl text-navy">Timeline</h2>
        {plan.timeline.map((item) => (
          <div key={item.title} className="mt-2">
            <p className="text-sm">
              <strong>{item.when}:</strong> {item.title} — {item.detail}
            </p>
            <BookingLink href={item.bookingUrl} label="Reserve" className="mt-2 no-print" />
          </div>
        ))}
      </section>
      <section>
        <h2 className="font-display text-2xl text-navy">Assumptions</h2>
        <ul className="list-disc pl-4 text-[13px]">
          {plan.assumptions.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    </article>
  );
}
